import { View } from "lucide-react";

// Localization system for Pochoclo.tv
export type SupportedLanguage = 'en-US' | 'pt-BR' | 'es-CL' | 'es-AR' | 'es-LATAM';

// Map country codes to languages
export const countryToLanguage: Record<string, SupportedLanguage> = {
  // English
  'US': 'en-US',
  'GB': 'en-US',
  'CA': 'en-US',
  'AU': 'en-US',
  'NZ': 'en-US',
  'IE': 'en-US',
  'ZA': 'en-US',
  'IN': 'en-US',
  'SG': 'en-US',
  'MY': 'en-US',
  'PH': 'en-US',
  'KE': 'en-US',
  'NG': 'en-US',
  'EG': 'en-US',
  'IL': 'en-US',
  'JM': 'en-US',
  'TT': 'en-US',
  'BB': 'en-US',
  'BS': 'en-US',
  'BZ': 'en-US',
  'GY': 'en-US',
  'FK': 'en-US',

  // Portuguese
  'BR': 'pt-BR',
  'PT': 'pt-BR',

  // Spanish - Chile
  'CL': 'es-CL',

  // Spanish - Argentina
  'AR': 'es-AR',
  'UY': 'es-AR', // Uruguay uses similar Spanish to Argentina

  // Spanish - Latin America (default for other Spanish-speaking countries)
  'MX': 'es-LATAM',
  'CO': 'es-LATAM',
  'PE': 'es-LATAM',
  'VE': 'es-LATAM',
  'EC': 'es-LATAM',
  'BO': 'es-LATAM',
  'PY': 'es-LATAM',
  'CR': 'es-LATAM',
  'PA': 'es-LATAM',
  'GT': 'es-LATAM',
  'HN': 'es-LATAM',
  'SV': 'es-LATAM',
  'NI': 'es-LATAM',
  'DO': 'es-LATAM',
  'CU': 'es-LATAM',
  'SR': 'es-LATAM',
  'GF': 'es-LATAM',
  'ES': 'es-LATAM', // Spain uses Latin American Spanish as fallback
};

// Get language from country code
export function getLanguageFromCountry(countryCode: string): SupportedLanguage {
  return countryToLanguage[countryCode] || 'en-US';
}

// Translation keys interface
export interface Translations {
  // Common
  loading: string;
  error: string;
  search: string;
  backToHome: string;
  tryAgain: string;

  // Header
  siteTitle: string;
  siteSubtitle: string;
  country: string;

  // Home page
  welcomeTitle: string;
  welcomeSubtitle: string;
  searchPlaceholder: string;
  popularContent: string;
  loadingContent: string;
  yourViews: string;

  // Search
  searchResults: string;
  searchTMDB: string;
  noResultsFound: string;
  tryDifferentKeywords: string;
  cantFindWhatLookingFor: string;
  searching: string;
  foundResults: string;

  // TMDB Search
  tmdbSearchResults: string;
  showingAvailabilityFor: string;
  noResultsFoundFor: string;
  tryDifferentSearch: string;
  whereToWatch: string;
  noStreamingInfo: string;
  seeDetails: string;
  saving: string;

  // Content Detail
  overview: string;
  availableIn: string;
  watchNow: string;
  noStreamingInfoAvailable: string;
  contentNotFound: string;

  // Content types
  movie: string;
  tvSeries: string;

  // Errors
  configurationError: string;
  configurationErrorMessage: string;
  authenticationFailed: string;
  tooManyRequests: string;
  searchFailed: string;
  validationError: string;
  internalServerError: string;

  // Time and dates
  year: string;
  minutes: string;
  hours: string;

  // Streaming
  streamingPlatforms: string;
  availableOn: string;
  notAvailable: string;

  // No availability messages
  titleNotAvailableInCountry: string;
  tryDifferentCountryOrCheckLater: string;

  // SEO specific translations
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  movieSeoTitle: string;
  tvSeriesSeoTitle: string;
  searchSeoTitle: string;
  searchSeoDescription: string;

  // TMDB Search specific
  theTitle: string;
  wasNotFound: string;
}

// English translations
const enUS: Translations = {
  // Common
  loading: 'Loading...',
  error: 'Error',
  search: 'Search',
  backToHome: 'Back to Home',
  tryAgain: 'Try Again',

  // Header
  siteTitle: 'Cornpop.tv',
  siteSubtitle: 'Find where to watch',
  country: 'Country',

  // Home page
  welcomeTitle: 'Discover Pochoclo',
  welcomeSubtitle: 'All your favorite movies and TV shows across streaming platforms',
  searchPlaceholder: 'Search for movies and TV shows...',
  popularContent: 'Popular Content',
  loadingContent: 'Loading content...',
  yourViews: 'Recently Viewed',

  // Search
  searchResults: 'Search Results',
  searchTMDB: 'Search TMDB',
  noResultsFound: 'No results found',
  tryDifferentKeywords: 'Try searching with different keywords',
  cantFindWhatLookingFor: 'Can\'t find what you\'re looking for?',
  searching: 'Searching...',
  foundResults: 'Found {count} results for "{query}" in {country}',

  // TMDB Search
  tmdbSearchResults: 'TMDB Search Results',
  showingAvailabilityFor: 'Showing availability for {country}',
  noResultsFoundFor: 'No results found for "{query}"',
  tryDifferentSearch: 'Try a different search term',
  whereToWatch: 'Where to Watch',
  noStreamingInfo: 'No streaming info available',
  seeDetails: 'See Details',
  saving: 'Saving...',

  // Content Detail
  overview: 'Overview',
  availableIn: 'Available in {country}',
  watchNow: 'Watch Now',
  noStreamingInfoAvailable: 'No streaming information available for this content.',
  contentNotFound: 'Content not found',

  // Content types
  movie: 'Movie',
  tvSeries: 'TV Series',

  // Errors
  configurationError: 'Configuration Error',
  configurationErrorMessage: 'The application is missing required Supabase configuration.',
  authenticationFailed: 'Authentication failed. Please refresh the page.',
  tooManyRequests: 'Too many requests. Please wait before searching again.',
  searchFailed: 'Search failed. Please try again.',
  validationError: 'Validation error',
  internalServerError: 'Internal server error',

  // Time and dates
  year: 'year',
  minutes: 'min',
  hours: 'h',

  // Streaming
  streamingPlatforms: 'Streaming Platforms',
  availableOn: 'Available on',
  notAvailable: 'Not available',

  // No availability messages
  titleNotAvailableInCountry: 'This title is not yet available to watch in {country}',
  tryDifferentCountryOrCheckLater: 'Try selecting a different country or check back later',

  // SEO specific translations
  seoTitle: 'Cornpop.tv - Find Where to Watch Movies & TV Shows | Streaming Guide',
  seoDescription: 'Discover where to watch your favorite movies and TV shows across 65+ countries. Find streaming availability on Netflix, Amazon Prime, Disney+, and more platforms instantly.',
  seoKeywords: 'streaming guide, where to watch movies, TV shows streaming, Netflix movies, Amazon Prime Video, Disney Plus, streaming platforms, movie finder, TV series finder',
  movieSeoTitle: 'Where to Watch {title} ({year}) | Streaming Guide',
  tvSeriesSeoTitle: 'Where to Watch {title} TV Series ({year}) | Streaming Guide',
  searchSeoTitle: 'Search Results for "{query}" | Cornpop.tv',
  searchSeoDescription: 'Find where to watch "{query}" across streaming platforms. Discover movies and TV shows availability in your country.',

  // TMDB Search specific
  theTitle: 'The title',
  wasNotFound: 'was not found',
};

// Portuguese (Brazil) translations
const ptBR: Translations = {
  // Common
  loading: 'Carregando...',
  error: 'Erro',
  search: 'Buscar',
  backToHome: 'Voltar ao Início',
  tryAgain: 'Tentar Novamente',

  // Header
  siteTitle: 'Cornpop.tv',
  siteSubtitle: 'Descubra onde assistir',
  country: 'País',

  // Home page
  welcomeTitle: 'Descubra o Pochoclo',
  welcomeSubtitle: 'Todos os seus filmes e séries favoritos nas plataformas de streaming',
  searchPlaceholder: 'Busque por filmes e séries...',
  popularContent: 'Conteúdo Popular',
  loadingContent: 'Carregando conteúdo...',
  yourViews: 'Visto Recentemente',

  // Search
  searchResults: 'Resultados da Busca',
  searchTMDB: 'Buscar no TMDB',
  noResultsFound: 'Nenhum resultado encontrado',
  tryDifferentKeywords: 'Tente buscar com palavras-chave diferentes',
  cantFindWhatLookingFor: 'Não consegue encontrar o que procura?',
  searching: 'Buscando...',
  foundResults: 'Encontrados {count} resultados para "{query}" no {country}',

  // TMDB Search
  tmdbSearchResults: 'Resultados da Busca TMDB',
  showingAvailabilityFor: 'Mostrando disponibilidade para {country}',
  noResultsFoundFor: 'Nenhum resultado encontrado para "{query}"',
  tryDifferentSearch: 'Tente um termo de busca diferente',
  whereToWatch: 'Onde Assistir',
  noStreamingInfo: 'Informações de streaming não disponíveis',
  seeDetails: 'Ver Detalhes',
  saving: 'Salvando...',

  // Content Detail
  overview: 'Sinopse',
  availableIn: 'Disponível no {country}',
  watchNow: 'Assistir Agora',
  noStreamingInfoAvailable: 'Nenhuma informação de streaming disponível para este conteúdo.',
  contentNotFound: 'Conteúdo não encontrado',

  // Content types
  movie: 'Filme',
  tvSeries: 'Série',

  // Errors
  configurationError: 'Erro de Configuração',
  configurationErrorMessage: 'A aplicação está com configurações do Supabase em falta.',
  authenticationFailed: 'Falha na autenticação. Por favor, atualize a página.',
  tooManyRequests: 'Muitas solicitações. Aguarde antes de buscar novamente.',
  searchFailed: 'Busca falhou. Tente novamente.',
  validationError: 'Erro de validação',
  internalServerError: 'Erro interno do servidor',

  // Time and dates
  year: 'ano',
  minutes: 'min',
  hours: 'h',

  // Streaming
  streamingPlatforms: 'Plataformas de Streaming',
  availableOn: 'Disponível em',
  notAvailable: 'Não disponível',

  // No availability messages
  titleNotAvailableInCountry: 'Este título ainda não está disponível para assistir no {country}',
  tryDifferentCountryOrCheckLater: 'Tente selecionar um país diferente ou verifique novamente mais tarde',

  // SEO specific translations
  seoTitle: 'Cornpop.tv - Descubra Onde Assistir Filmes e Séries | Guia de Streaming',
  seoDescription: 'Descubra onde assistir seus filmes e séries favoritos em mais de 65 países. Encontre disponibilidade no Netflix, Amazon Prime, Disney+, e outras plataformas instantaneamente.',
  seoKeywords: 'guia de streaming, onde assistir filmes, streaming de séries, filmes Netflix, Amazon Prime Video, Disney Plus, plataformas de streaming, localizador de filmes, localizador de séries',
  movieSeoTitle: 'Onde Assistir {title} ({year}) | Guia de Streaming',
  tvSeriesSeoTitle: 'Onde Assistir {title} Série ({year}) | Guia de Streaming',
  searchSeoTitle: 'Resultados da Busca para "{query}" | Cornpop.tv',
  searchSeoDescription: 'Encontre onde assistir "{query}" nas plataformas de streaming. Descubra disponibilidade de filmes e séries no seu país.',

  // TMDB Search specific
  theTitle: 'O título',
  wasNotFound: 'não foi encontrado',
};

// Spanish (Chile) translations
const esCL: Translations = {
  // Common
  loading: 'Cargando...',
  error: 'Error',
  search: 'Buscar',
  backToHome: 'Volver al Inicio',
  tryAgain: 'Intentar de Nuevo',

  // Header
  siteTitle: 'Cornpop.tv',
  siteSubtitle: 'Encuentra dónde ver',
  country: 'País',

  // Home page
  welcomeTitle: 'Descubre Pochoclo',
  welcomeSubtitle: 'Todas tus películas y series favoritas en las plataformas de streaming',
  searchPlaceholder: 'Busca películas y series...',
  popularContent: 'Contenido Popular',
  loadingContent: 'Cargando contenido...',
  yourViews: 'Recientemente Visto',

  // Search
  searchResults: 'Resultados de Búsqueda',
  searchTMDB: 'Buscar en TMDB',
  noResultsFound: 'No se encontraron resultados',
  tryDifferentKeywords: 'Intenta buscar con palabras clave diferentes',
  cantFindWhatLookingFor: '¿No encuentras lo que buscas?',
  searching: 'Buscando...',
  foundResults: 'Se encontraron {count} resultados para "{query}" en {country}',

  // TMDB Search
  tmdbSearchResults: 'Resultados de Búsqueda TMDB',
  showingAvailabilityFor: 'Mostrando disponibilidad para {country}',
  noResultsFoundFor: 'No se encontraron resultados para "{query}"',
  tryDifferentSearch: 'Prueba con un término de búsqueda diferente',
  whereToWatch: 'Dónde Ver',
  noStreamingInfo: 'Información de streaming no disponible',
  seeDetails: 'Ver Detalles',
  saving: 'Guardando...',

  // Content Detail
  overview: 'Sinopsis',
  availableIn: 'Disponible en {country}',
  watchNow: 'Ver Ahora',
  noStreamingInfoAvailable: 'No hay información de streaming disponible para este contenido.',
  contentNotFound: 'Contenido no encontrado',

  // Content types
  movie: 'Película',
  tvSeries: 'Serie',

  // Errors
  configurationError: 'Error de Configuración',
  configurationErrorMessage: 'La aplicación no tiene la configuración requerida de Supabase.',
  authenticationFailed: 'Falló la autenticación. Por favor, actualiza la página.',
  tooManyRequests: 'Demasiadas solicitudes. Espera antes de buscar de nuevo.',
  searchFailed: 'La búsqueda falló. Inténtalo de nuevo.',
  validationError: 'Error de validación',
  internalServerError: 'Error interno del servidor',

  // Time and dates
  year: 'año',
  minutes: 'min',
  hours: 'h',

  // Streaming
  streamingPlatforms: 'Plataformas de Streaming',
  availableOn: 'Disponible en',
  notAvailable: 'No disponible',

  // No availability messages
  titleNotAvailableInCountry: 'Este título aún no está disponible para ver en {country}',
  tryDifferentCountryOrCheckLater: 'Prueba seleccionando un país diferente o vuelve a revisar más tarde',

  // SEO specific translations
  seoTitle: 'Cornpop.tv - Encuentra Dónde Ver Películas y Series | Guía de Streaming',
  seoDescription: 'Descubre dónde ver tus películas y series favoritas en más de 65 países. Encuentra disponibilidad en Netflix, Amazon Prime, Disney+, y otras plataformas al instante.',
  seoKeywords: 'guía de streaming, dónde ver películas, streaming de series, películas Netflix, Amazon Prime Video, Disney Plus, plataformas de streaming, buscador de películas, buscador de series',
  movieSeoTitle: 'Dónde Ver {title} ({year}) | Guía de Streaming',
  tvSeriesSeoTitle: 'Dónde Ver {title} Serie ({year}) | Guía de Streaming',
  searchSeoTitle: 'Resultados de Búsqueda para "{query}" | Cornpop.tv',
  searchSeoDescription: 'Encuentra dónde ver "{query}" en plataformas de streaming. Descubre disponibilidad de películas y series en tu país.',

  // TMDB Search specific
  theTitle: 'El título',
  wasNotFound: 'no fue encontrado',
};

// Spanish (Argentina) translations
const esAR: Translations = {
  // Common
  loading: 'Cargando...',
  error: 'Error',
  search: 'Buscar',
  backToHome: 'Volver al Inicio',
  tryAgain: 'Intentar de Nuevo',

  // Header
  siteTitle: 'Cornpop.tv',
  siteSubtitle: 'Encontrá dónde ver',
  country: 'País',

  // Home page
  welcomeTitle: 'Descubrí Pochoclo',
  welcomeSubtitle: 'Todas tus películas y series favoritas en las plataformas de streaming',
  searchPlaceholder: 'Buscá películas y series...',
  popularContent: 'Contenido Popular',
  loadingContent: 'Cargando contenido...',
  yourViews: 'Recientemente Visto',

  // Search
  searchResults: 'Resultados de Búsqueda',
  searchTMDB: 'Buscar en TMDB',
  noResultsFound: 'No se encontraron resultados',
  tryDifferentKeywords: 'Probá buscando con palabras clave diferentes',
  cantFindWhatLookingFor: '¿No encontrás lo que buscás?',
  searching: 'Buscando...',
  foundResults: 'Se encontraron {count} resultados para "{query}" en {country}',

  // TMDB Search
  tmdbSearchResults: 'Resultados de Búsqueda TMDB',
  showingAvailabilityFor: 'Mostrando disponibilidad para {country}',
  noResultsFoundFor: 'No se encontraron resultados para "{query}"',
  tryDifferentSearch: 'Probá con un término de búsqueda diferente',
  whereToWatch: 'Dónde Ver',
  noStreamingInfo: 'Información de streaming no disponible',
  seeDetails: 'Ver Detalles',
  saving: 'Guardando...',

  // Content Detail
  overview: 'Sinopsis',
  availableIn: 'Disponible en {country}',
  watchNow: 'Ver Ahora',
  noStreamingInfoAvailable: 'No hay información de streaming disponible para este contenido.',
  contentNotFound: 'Contenido no encontrado',

  // Content types
  movie: 'Película',
  tvSeries: 'Serie',

  // Errors
  configurationError: 'Error de Configuración',
  configurationErrorMessage: 'La aplicación no tiene la configuración requerida de Supabase.',
  authenticationFailed: 'Falló la autenticación. Por favor, actualizá la página.',
  tooManyRequests: 'Demasiadas solicitudes. Esperá antes de buscar de nuevo.',
  searchFailed: 'La búsqueda falló. Intentalo de nuevo.',
  validationError: 'Error de validación',
  internalServerError: 'Error interno del servidor',

  // Time and dates
  year: 'año',
  minutes: 'min',
  hours: 'h',

  // Streaming
  streamingPlatforms: 'Plataformas de Streaming',
  availableOn: 'Disponible en',
  notAvailable: 'No disponible',

  // No availability messages
  titleNotAvailableInCountry: 'Este título aún no está disponible para ver en {country}',
  tryDifferentCountryOrCheckLater: 'Probá seleccionando un país diferente o volvé a revisar más tarde',

  // SEO specific translations
  seoTitle: 'Cornpop.tv - Encuentra Dónde Ver Películas y Series | Guía de Streaming',
  seoDescription: 'Descubre dónde ver tus películas y series favoritas en más de 65 países. Encuentra disponibilidad en Netflix, Amazon Prime, Disney+, y otras plataformas al instante.',
  seoKeywords: 'guía de streaming, dónde ver películas, streaming de series, películas Netflix, Amazon Prime Video, Disney Plus, plataformas de streaming, buscador de películas, buscador de series',
  movieSeoTitle: 'Dónde Ver {title} ({year}) | Guía de Streaming',
  tvSeriesSeoTitle: 'Dónde Ver {title} Serie ({year}) | Guía de Streaming',
  searchSeoTitle: 'Resultados de Búsqueda para "{query}" | Cornpop.tv',
  searchSeoDescription: 'Encuentra dónde ver "{query}" en plataformas de streaming. Descubre disponibilidad de películas y series en tu país.',

  // TMDB Search specific
  theTitle: 'El título',
  wasNotFound: 'no fue encontrado',
};

// Spanish (Latin America) translations
const esLATAM: Translations = {
  // Common
  loading: 'Cargando...',
  error: 'Error',
  search: 'Buscar',
  backToHome: 'Volver al Inicio',
  tryAgain: 'Intentar de Nuevo',

  // Header
  siteTitle: 'Cornpop.tv',
  siteSubtitle: 'Encuentra dónde ver',
  country: 'País',

  // Home page
  welcomeTitle: 'Descubre Pochoclo.tv',
  welcomeSubtitle: 'Todas tus películas y series favoritas en las plataformas de streaming',
  searchPlaceholder: 'Busca películas y series...',
  popularContent: 'Contenido Popular',
  loadingContent: 'Cargando contenido...',
  yourViews: 'Recientemente Visto',

  // Search
  searchResults: 'Resultados de Búsqueda',
  searchTMDB: 'Buscar en TMDB',
  noResultsFound: 'No se encontraron resultados',
  tryDifferentKeywords: 'Intenta buscar con palabras clave diferentes',
  cantFindWhatLookingFor: '¿No encuentras lo que buscas?',
  searching: 'Buscando...',
  foundResults: 'Se encontraron {count} resultados para "{query}" en {country}',

  // TMDB Search
  tmdbSearchResults: 'Resultados de Búsqueda TMDB',
  showingAvailabilityFor: 'Mostrando disponibilidad para {country}',
  noResultsFoundFor: 'No se encontraron resultados para "{query}"',
  tryDifferentSearch: 'Prueba con un término de búsqueda diferente',
  whereToWatch: 'Dónde Ver',
  noStreamingInfo: 'Información de streaming no disponible',
  seeDetails: 'Ver Detalles',
  saving: 'Guardando...',

  // Content Detail
  overview: 'Sinopsis',
  availableIn: 'Disponible en {country}',
  watchNow: 'Ver Ahora',
  noStreamingInfoAvailable: 'No hay información de streaming disponible para este contenido.',
  contentNotFound: 'Contenido no encontrado',

  // Content types
  movie: 'Película',
  tvSeries: 'Serie',

  // Errors
  configurationError: 'Error de Configuración',
  configurationErrorMessage: 'La aplicación no tiene la configuración requerida de Supabase.',
  authenticationFailed: 'Falló la autenticación. Por favor, actualiza la página.',
  tooManyRequests: 'Demasiadas solicitudes. Espera antes de buscar de nuevo.',
  searchFailed: 'La búsqueda falló. Inténtalo de nuevo.',
  validationError: 'Error de validación',
  internalServerError: 'Error interno del servidor',

  // Time and dates
  year: 'año',
  minutes: 'min',
  hours: 'h',

  // Streaming
  streamingPlatforms: 'Plataformas de Streaming',
  availableOn: 'Disponible en',
  notAvailable: 'No disponible',

  // No availability messages
  titleNotAvailableInCountry: 'Este título aún no está disponible para ver en {country}',
  tryDifferentCountryOrCheckLater: 'Prueba seleccionando un país diferente o vuelve a revisar más tarde',

  // SEO specific translations
  seoTitle: 'Cornpop.tv - Encuentra Dónde Ver Películas y Series | Guía de Streaming',
  seoDescription: 'Descubre dónde ver tus películas y series favoritas en más de 65 países. Encuentra disponibilidad en Netflix, Amazon Prime, Disney+, y otras plataformas al instante.',
  seoKeywords: 'guía de streaming, dónde ver películas, streaming de series, películas Netflix, Amazon Prime Video, Disney Plus, plataformas de streaming, buscador de películas, buscador de series',
  movieSeoTitle: 'Dónde Ver {title} ({year}) | Guía de Streaming',
  tvSeriesSeoTitle: 'Dónde Ver {title} Serie ({year}) | Guía de Streaming',
  searchSeoTitle: 'Resultados de Búsqueda para "{query}" | Cornpop.tv',
  searchSeoDescription: 'Encuentra dónde ver "{query}" en plataformas de streaming. Descubre disponibilidad de películas y series en tu país.',

  // TMDB Search specific
  theTitle: 'El título',
  wasNotFound: 'no fue encontrado',
};

// Translation dictionary
export const translations: Record<SupportedLanguage, Translations> = {
  'en-US': enUS,
  'pt-BR': ptBR,
  'es-CL': esCL,
  'es-AR': esAR,
  'es-LATAM': esLATAM,
};

// Get translations for a specific language
export function getTranslations(language: SupportedLanguage): Translations {
  return translations[language] || translations['en-US'];
}

// Simple interpolation function for dynamic values
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key]?.toString() || match;
  });
}